import { useState } from 'react'
import { InputAdornment, InputBase } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import useRidesStore from '../../../stores/rides'
import moment, { Moment } from 'moment-timezone'
import { DateRange } from '@mui/x-date-pickers-pro'
import Autocomplete from '@mui/material/Autocomplete'

const filters = [
  { key: 'refs:', value: 'Reference IDs' },
  { key: 'uname:', value: 'Passenger Username' }
]

type MuiEvent = (
  React.KeyboardEvent<HTMLInputElement>
  ) & {
  defaultMuiPrevented?: boolean;
};

const Header: React.FC = () => {
  const updateSearchOptions = useRidesStore(state => state.updateSearchOptions)
  const search = useRidesStore(state => state.searchOptions.search)
  const from_datetime = useRidesStore(state => state.searchOptions.from_datetime)
  const to_datetime = useRidesStore(state => state.searchOptions.to_datetime)

  const [searchValue, setSearchValue] = useState<string>(search)
  const [dateRange, setDateRange] = useState<DateRange<Moment>>(
    [from_datetime ? moment(from_datetime) : null, to_datetime ? moment(to_datetime) : null]
  )

  const handleUpdateDateRange = (value: DateRange<Moment>) => {
    updateSearchOptions({
      from_datetime: value[0]?.isValid() ? value[0].format('YYYY-MM-DD') : null,
      to_datetime: value[1]?.isValid() ? value[1].format('YYYY-MM-DD') : null
    })
  }

  return (
    <div className="header">
      <Autocomplete
        freeSolo
        disablePortal
        fullWidth
        disableCloseOnSelect
        autoComplete
        value={searchValue}
        inputValue={searchValue}
        options={filters}
        clearOnBlur={false}
        className="search-autocomplete"
        onBlur={() => {
          updateSearchOptions({ search: searchValue })
        }}
        onKeyDown={(e: MuiEvent) => {
          if (['Enter', 'Escape'].includes(e.key)) {
            (document.activeElement as HTMLElement).blur()
            e.defaultMuiPrevented = true
          }
        }}
        onChange={(event, value, reason) => {
          if (reason === 'selectOption' && typeof value === 'object') {
            setSearchValue(value?.key || '')
          }
        }}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            setSearchValue(value)
          }
        }}
        filterOptions={options => options}
        isOptionEqualToValue={option => searchValue.includes(option.key)}
        renderOption={(props, option) => {
          if (option.key === null) {
            return 'Suggested Filters'
          }
          return (
            <li {...props}>
              <span className="key">{option.key}</span>
              {option.value}
            </li>
          )
        }}
        slotProps={{
          paper: {
            className: 'search-options'
          }
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') {
            return option
          }
          return option.key
        }}
        renderInput={params => (
          <InputBase
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            fullWidth
            className="search"
            placeholder="Search rides"
            size="medium"
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            } />
        )}
      />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DateRangePicker
          value={dateRange}
          onAccept={handleUpdateDateRange}
          onChange={setDateRange}
          className="date-range-picker"
          localeText={{ start: 'Start Date', end: 'End Date' }}
          slotProps={{
            textField: {
              size: 'small',
              className: 'input'
            }
          }}
        />
      </LocalizationProvider>
    </div>
  )
}

export default Header
